import React, { useContext, useRef, useState, useCallback } from "react";
import { AppContext } from "@/providers/AppContext";
import Link from "next/link";
import { useAuth } from "@/providers/AuthenticationProvider";
import { AuthStatus } from "@/types/auth/AuthStatus";
import { pkceUserProfile } from "@/types/auth/pkceUserProfile";
import {
  AuthMenuWrapper,
  HeaderMenuTop,
  HeaderTop,
  HeaderTopBelow,
  HeaderTopInner,
  HeaderTopSection,
  HeaderWidthContainer,
  HeaderWrapper,
  IconButton,
  LogoLink,
  SvgIconWhite,
  TopHeaderText
} from "./HeaderComponents";
import AppLogoWhite from "@/assets/images/AppLogo.svg";
import { DESKTOP_HEADER_MEDIA_QUERY } from "./HeaderConstants";
import { Box } from "@mui/material";
import { Profile } from "@digital-hig/icon";
import { theme } from "@/config/theme";
import { ProfileMenu } from "@/components/Header/ProfileMenu";
import { ItemCatEnvSwitcher } from "@/components/Header/ItemCatEnvSwitcher";
import Typography from "@digital-hig/mui/Typography";
import Button from "@digital-hig/mui/Button";
import LinkButton from "@/components/common/LinkButton";
import SvgIcon from "@digital-hig/mui/SvgIcon";
import { Feedback } from "@digital-hig/icon";
import Modal from "@digital-hig/mui/Modal";
import { FormControlLabel, RadioGroup, Radio, FormControl, Input, InputLabel } from "@mui/material";
import CompleteIcon from "@digital-hig/icon/lib/esm/display-icons/complete";
import HeaderMenu from "./HeaderMenu";
import { getCurrentUri, PAGE } from "@/utilities/getEnvironment";
import { useRouter } from "next/router";

export function HeaderUI({
  authStatus,
  sticky = false,
  userProfile,
  vpnConnect,
  accessToken,
  signIn,
  signOut
}: {
  authStatus: AuthStatus;
  sticky?: boolean;
  userProfile: pkceUserProfile | null;
  vpnConnect: number;
  accessToken: string;
  signIn: () => Promise<null>;
  signOut: () => Promise<null>;
}) {
  const router = useRouter();
  const { itemCreateAllowed, admin } = useContext(AppContext);
  const [openFeedback, setOpenFeedback] = useState(false);
  const [feedbackValue, setFeedbackValue] = useState("");
  const [completeFeedback, setCompleteFeedback] = useState(false);

  const headerRef = useRef<HTMLElement>(null);
  const [profileMenuIsOpen, setProfileMenuIsOpen] = useState(false);

  const openProfileMenu = useCallback(() => {
    setProfileMenuIsOpen(true);
  }, []);

  const closeProfileMenu = useCallback(() => {
    setProfileMenuIsOpen(false);
  }, []);

  const feedbackOptionChange = (e: any) => {
    setFeedbackValue(e.target.value);
  };

  const closeFeedbackModal = () => {
    setFeedbackValue("");
    setOpenFeedback(false);
  };

  const submitFeedback = () => {
    setOpenFeedback(false);
    setCompleteFeedback(true);
  };

  return (
    <>
      <HeaderWrapper data-testid="test-header-ui" sticky={sticky} ref={headerRef}>
        <HeaderTop>
          <HeaderWidthContainer>
            <HeaderTopInner>
              <HeaderTopSection>
                <Link href="/" aria-label="Autodesk Logo" passHref={true}>
                  <LogoLink>
                    <AppLogoWhite
                      data-testid="header-logo"
                      css={{
                        width: 250,
                        [DESKTOP_HEADER_MEDIA_QUERY]: {
                          width: 250
                        }
                      }}
                    />
                  </LogoLink>
                </Link>
              </HeaderTopSection>
              <HeaderTopSection>
                <AuthMenuWrapper>
                  {/* {authStatus === "pending" ? (
                                    <Skeleton variant="rectangular" height={24} width={"100%"} />
                                ) : null}

                                {authStatus === "logged-out" ? (
                                <IconButton onClick={signIn} data-testid="sign-in-button">
                                    <SvgIconWhite size="large">
                                        <Profile />
                                    </SvgIconWhite>
                                    <TopHeaderText
                                    css={{
                                        fontWeight: 800,
                                        marginLeft: theme.primitives.spacings[2],
                                    }}
                                    >
                                    Sign in
                                    </TopHeaderText>
                                </IconButton>
                                ) : null} */}

                  {authStatus === "logged-in" && userProfile ? (
                    <ProfileMenu
                      constrainHeight={sticky}
                      maxConstrainedHeight={"calc(100vh - 7.5rem)"}
                      // menuItems={profileMenu}
                      userProfile={userProfile}
                      isOpen={profileMenuIsOpen}
                      onOpen={openProfileMenu}
                      onClose={closeProfileMenu}
                      signOut={signOut}
                    />
                  ) : null}
                </AuthMenuWrapper>
              </HeaderTopSection>
            </HeaderTopInner>
          </HeaderWidthContainer>
        </HeaderTop>
        {authStatus === "logged-in" && (
          <>
            <HeaderMenuTop>
              <HeaderWidthContainer>
                <HeaderTopInner>
                  <HeaderTopSection>
                    <HeaderMenu />
                  </HeaderTopSection>
                </HeaderTopInner>
              </HeaderWidthContainer>
            </HeaderMenuTop>
          </>
        )}
      </HeaderWrapper>
      {!(authStatus !== "logged-in" || !accessToken || vpnConnect == 0 || vpnConnect == -1) && getCurrentUri() !== PAGE.ITEMS && getCurrentUri() !== PAGE.SUBMIT_ENGINE_CONFIG && getCurrentUri() !== PAGE.ENGINE_CONFIG_PUBLISHED && (
        <Box pb={7}>
          <HeaderWidthContainer>
            <Box pt={7} pb={3} sx={{ display: "flex", justifyContent: "space-between", borderBottom: "1px solid #808080" }}>
              <Typography variant="headline-small">{`Publishing Platform - ${getCurrentUri()}`}</Typography>
              {(getCurrentUri() === PAGE.ALL_JOBS || getCurrentUri() === PAGE.MY_JOBS) && (
                <Box textAlign={"right"}>
                  <LinkButton href="/submitjob">Create New Job</LinkButton>
                </Box>
              )}
              {(getCurrentUri() === PAGE.ALL_NOTARIZATIONS || getCurrentUri() === PAGE.MY_NOTARIZATIONS) && (
                <Box textAlign={"right"}>
                  <LinkButton href="/submitnotarization">Create New Notarization</LinkButton>
                </Box>
              )}
              {(getCurrentUri() === PAGE.ALL_ITEMS || getCurrentUri() === PAGE.MY_ITEMS) && (
                <Box sx={{ display: "flex", alignItems: "center", justifyContent: "flex-end", gap: 2 }}>
                  <ItemCatEnvSwitcher />
                  {/* Only MDM/NPI get Create New Item — see AppContext.tsx's itemCreateAllowed
                      comment for how this is derived without a dedicated BE endpoint. */}
                  {itemCreateAllowed && (
                    <LinkButton href="/submititem">Create New Item</LinkButton>
                  )}
                </Box>
              )}
              {getCurrentUri() === PAGE.JOB_PROFILES && (
                <Box textAlign={"right"}>
                  <LinkButton href="/submitJobProfile">+ Create Profile</LinkButton>
                </Box>
              )}
              {/* Viewing is open to everyone; only admins can add (aprs accepts POST /engine-config from admins only). */}
              {(getCurrentUri() === PAGE.ALL_ENGINE_CONFIG || getCurrentUri() === PAGE.MY_ENGINE_CONFIG) && admin && (
                <Box textAlign={"right"}>
                  <Button
                    onClick={() => {
                      router.push("/submitengineconfig");
                    }}
                  >
                    Add Product Mapping
                  </Button>
                </Box>
              )}
            </Box>
          </HeaderWidthContainer>
          <Modal
            type="modal"
            handleClose={closeFeedbackModal}
            open={openFeedback}
            title="Give feedback"
            // AlertIcon={InfoIcon}
            content={
              <Box mt={4} sx={{ minWidth: "450px" }}>
                <Typography mb={4} variant="headline-smallest">
                  How satisfied are you with Autodesk Publishing Platform
                </Typography>
                <RadioGroup onChange={feedbackOptionChange} value={feedbackValue}>
                  <FormControlLabel control={<Radio />} label="Very dissatisfied" value="Very dissatisfied" />
                  <FormControlLabel control={<Radio />} label="Dissatisfied" value="Dissatisfied" />
                  <FormControlLabel control={<Radio />} label="Neither satisfied nor dissatisfied" value="Neither satisfied nor dissatisfied" />
                  <FormControlLabel control={<Radio />} label="Satisfied" value="Satisfied" />
                  <FormControlLabel control={<Radio />} label="Very satisfied" value="Very satisfied" />
                </RadioGroup>
                {feedbackValue != "" && (
                  <Box mt={4}>
                    <FormControl fullWidth>
                      <InputLabel htmlFor="basic-text-area">Anything else you would like to share?</InputLabel>
                      <Input id="basic-text-area" placeholder="I have feedback on..." multiline rows={7} />
                    </FormControl>
                  </Box>
                )}
                <Box mt={6} pt={6} sx={{ textAlign: "right" }}>
                  <Button onClick={submitFeedback} disabled={feedbackValue == ""}>
                    Submit
                  </Button>
                </Box>
              </Box>
            }
          />
          <Modal
            className="completeFeedbackModal"
            handleClose={() => setCompleteFeedback(false)}
            open={completeFeedback}
            title="Feedback submitted. Thank you&nbsp;&nbsp;"
            type="alert"
            actions={
              <Box>
                <Button onClick={() => setCompleteFeedback(false)}>Close</Button>
              </Box>
            }
            content={
              <Typography mt={2} sx={{ maxWidth: "300px" }}>
                Your feedback was received, we use this to improve your experience.
              </Typography>
            }
            AlertIcon={CompleteIcon}
          />
        </Box>
      )}
    </>
  );
}

function HeaderLoader({ sticky }: { sticky: boolean }) {
  const { signIn, signOut, userProfile, authStatus, vpnConnect, accessToken } = useAuth();

  return (
    <HeaderUI
      authStatus={authStatus}
      userProfile={userProfile}
      vpnConnect={vpnConnect}
      accessToken={accessToken}
      signIn={signIn}
      signOut={signOut}
      sticky={sticky}
    />
  );
}

export const Header = React.memo(HeaderLoader);
